const REDEMPTION_TYPE_MAP = {
  BANK_ACCOUNT: 'cash',
  WPS: 'savings',
  VOUCHER: 'gift_cards',
};

export function transformSummary(raw) {
  if (!raw) {
    return raw;
  }
  return {
    total_granted_minor: raw.total_granted_minor ?? 0,
    total_redeemed_minor: raw.total_redeemed_minor ?? 0,
    awards_granted: raw.awards_count ?? 0,
    user_participation: Math.round(raw.participation_percent ?? 0),
  };
}

// Stacked bars stop being readable past a handful of bands, so the chart keeps the
// biggest spenders and rolls the tail into a single "Other" series.
export const MAX_SPEND_CHART_SERIES = 5;

const pivotSpendByPeriod = (rows, seriesNameFor) => {
  const periodSet = new Set();
  const seriesData = new Map();

  for (const row of rows) {
    periodSet.add(row.period);
    const name = seriesNameFor(row);
    if (!seriesData.has(name)) {
      seriesData.set(name, new Map());
    }
    const existing = seriesData.get(name).get(row.period) || 0;
    seriesData.get(name).set(row.period, existing + row.amount_minor);
  }

  const labels = [...periodSet].sort();

  const datasets = [];
  for (const [name, periodMap] of seriesData) {
    datasets.push({
      name,
      granted_values_minor: labels.map(p => periodMap.get(p) || 0),
    });
  }

  return { labels, datasets };
};

export function transformSpendOverTime(raw) {
  if (!raw || !raw.data) {
    return { labels: [], datasets: [] };
  }
  return pivotSpendByPeriod(raw.data, row => row.division_name || 'Unassigned');
}

export function transformSpendOverTimeByCategory(raw) {
  if (!raw || !raw.data) {
    return { labels: [], datasets: [] };
  }
  return pivotSpendByPeriod(raw.data, row => row.category || 'Uncategorised');
}

const datasetTotal = dataset => (dataset.granted_values_minor || []).reduce((sum, value) => sum + (value ?? 0), 0);

export function capSpendDatasets(datasets, max = MAX_SPEND_CHART_SERIES) {
  if (!datasets || datasets.length <= max) {
    return datasets || [];
  }

  const sorted = [...datasets].sort((a, b) => datasetTotal(b) - datasetTotal(a));
  const kept = sorted.slice(0, max);
  const rest = sorted.slice(max);
  const otherValues = kept[0].granted_values_minor.map((_, index) =>
    rest.reduce((sum, dataset) => sum + (dataset.granted_values_minor[index] ?? 0), 0),
  );

  return [...kept, { name: 'Other', granted_values_minor: otherValues }];
}

export function transformRedemptionBreakdown(raw) {
  const result = {
    cash: { amount_minor: 0, percentage: 0 },
    savings: { amount_minor: 0, percentage: 0 },
    gift_cards: { amount_minor: 0, percentage: 0 },
  };

  if (!raw || !raw.data) {
    return result;
  }

  for (const row of raw.data) {
    const key = REDEMPTION_TYPE_MAP[row.redemption_type];
    if (key) {
      result[key] = {
        amount_minor: row.amount_minor ?? 0,
        percentage: row.percentage ?? 0,
      };
    }
  }

  return result;
}

export function transformEngagement(raw) {
  if (!raw) {
    return null;
  }

  const extractSeries = series => {
    if (!series) {
      return { total: 0, change: 0, trend: [] };
    }
    return {
      total: series.total ?? 0,
      change: series.percent_change ?? 0,
      trend: (series.daily_values || []).map(dp => dp.value),
    };
  };

  const sent = extractSeries(raw.recognitions_sent);
  const received = extractSeries(raw.recognitions_received);
  const participation = extractSeries(raw.participation_rate);

  return {
    recognition_sent: sent.total,
    recognition_sent_change_pct: sent.change,
    recognition_sent_trend: sent.trend,
    nominations_received: received.total,
    nominations_received_change_pct: received.change,
    nominations_received_trend: received.trend,
    employee_participation: participation.total,
    employee_participation_change_pct: participation.change,
    employee_participation_trend: participation.trend,
  };
}

export function transformTopEmployees(raw) {
  if (!raw || !raw.data) {
    return [];
  }

  return raw.data.map(emp => ({
    employee_id: emp.employee_id,
    full_name: emp.full_name,
    email: emp.work_email ?? '',
    nominations_count: emp.nominations_received ?? 0,
    recognitions_count: emp.recognitions_count ?? 0,
  }));
}

export function transformTopRecognisers(raw) {
  if (!raw || !raw.data) {
    return [];
  }

  return raw.data.map(rec => ({
    employee_id: rec.employee_id,
    full_name: rec.full_name,
    sent_count: rec.nominations_sent ?? 0,
    is_portal_admin: rec.is_portal_admin,
  }));
}

export function transformQualities(raw) {
  if (!raw || !raw.data) {
    return null;
  }

  const qualities = raw.data.map(row => ({
    quality_id: row.value_name,
    quality_name: row.value_name,
    count: row.count ?? 0,
    percentage: row.percentage ?? 0,
    top_employees: [],
  }));

  return { qualities };
}
